import { InformationPad } from '../../../components/shared';
import {
  getTimeFormatForStaticView,
  getDateFormat,
  mergedTwoPhoneNumber,
  isArrayWithLength,
  checkNAOrReturnPercentValue,
  formatTaxPercentValue,
} from '../../../utils/common-methods';
import { isArcadeOrGameRoomType } from '../../../hooks';
import { ControlledTooltips } from '../../../components/shared';
import YesNoIndicator from '../../../components/shared/YesNoIndicator';
import { SUB_LOCATION_DETAIL_LABEL } from '../../../constants/accordionFields';
import { FEED_SOURCE_OPTIONS, CREDIT_CASH_OPTIONS } from '../../../constants/locationConstants';
import { useGetProductCategoriesQuery } from '../../../services/Sublocation/SublocationService';
import { Tooltip, Chip } from '@mui/material';

export const getFeedSourceDisplayText = (feedSourceValue) => {
  if (!feedSourceValue) return 'N/A';

  if (Array.isArray(feedSourceValue)) {
    const displayTexts = feedSourceValue.map((value) => {
      const option = FEED_SOURCE_OPTIONS.find((opt) => opt.value === value);
      return option ? option.text : value;
    });
    return displayTexts.join(', ');
  } else if (typeof feedSourceValue === 'string') {
    const valueArray = feedSourceValue.split(',').map((item) => item.trim());
    const displayTexts = valueArray.map((value) => {
      const option = FEED_SOURCE_OPTIONS.find((opt) => opt.value === value);
      return option ? option.text : value;
    });
    return displayTexts.join(', ');
  }

  return feedSourceValue;
};

export const getCreditCashDisplayText = (creditCashValue) => {
  if (!creditCashValue) return 'N/A';

  const option = CREDIT_CASH_OPTIONS.find((opt) => opt.value === creditCashValue);
  if (option) {
    return option.text;
  }

  if (typeof creditCashValue === 'string') {
    return creditCashValue.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return creditCashValue;
};

const SubLocationDetailAccordion = ({ sublocationInfo, stateInfo }) => {
  const { currentData: productCategoriesData } = useGetProductCategoriesQuery();
  const leaseCCFeeRate =
    sublocationInfo?.leaseCreditCardFeeRate != null ? Number(sublocationInfo.leaseCreditCardFeeRate) : undefined;

  let productTypeStr = '';
  if (isArrayWithLength(sublocationInfo?.productCategories)) {
    let productNames = sublocationInfo?.productCategories?.map((item) => item?.name || '');
    productTypeStr = productNames?.join(', ');
  }

  const fields = [
    { label: SUB_LOCATION_DETAIL_LABEL.ZIPCODE, value: sublocationInfo?.code },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PHONE,
      value: mergedTwoPhoneNumber(sublocationInfo?.phone1, sublocationInfo?.phone2),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.LOCATION_TYPE,
      value: sublocationInfo?.sublocationType?.name,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.MANAGER,
      value: (
        <ControlledTooltips tooltipValue={sublocationInfo?.tooltipValue}>{sublocationInfo?.manager}</ControlledTooltips>
      ),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.OPEN_HOURS,
      value:
        sublocationInfo?.openHours && sublocationInfo?.closeHours
          ? `${getTimeFormatForStaticView(sublocationInfo?.openHours)}-${getTimeFormatForStaticView(
              sublocationInfo?.closeHours,
            )}`
          : null,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.START_DATE,
      value: getDateFormat(sublocationInfo?.startDate),
    },
    { label: SUB_LOCATION_DETAIL_LABEL.END_DATE, value: getDateFormat(sublocationInfo?.endDate) },
    {
      label: SUB_LOCATION_DETAIL_LABEL.UNINSTALL_DATE,
      value: sublocationInfo?.uninstallDate ? getDateFormat(sublocationInfo?.uninstallDate) : null,
    },
    { label: SUB_LOCATION_DETAIL_LABEL.ADDRESS1, value: sublocationInfo?.address1 },
    { label: SUB_LOCATION_DETAIL_LABEL.ADDRESS2, value: sublocationInfo?.address2 },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PRODUCT_TYPE,
      value: productTypeStr || 'N/A',
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.VENDOR_CODE,
      value: sublocationInfo?.vendor?.vendorCode,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.VENDOR_NAME,
      value: sublocationInfo?.vendor?.customerName,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.STATE_TAX,
      value: formatTaxPercentValue(stateInfo?.stateTaxHistories?.[0]?.stateTax),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.AREA_TAX,
      value: formatTaxPercentValue(sublocationInfo?.areaTaxHistories?.[0]?.areaTax),
    },
    { label: SUB_LOCATION_DETAIL_LABEL.MISC_TAX, value: checkNAOrReturnPercentValue(sublocationInfo?.miscTax) },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CC_FEE,
      value: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {checkNAOrReturnPercentValue(sublocationInfo?.creditCardFeeRate ?? leaseCCFeeRate)}
          <Tooltip
            title={
              sublocationInfo?.creditCardFeeRate != null ? 'overridden from Sublocation edit' : 'Lease Default CC Fee'
            }
            arrow
          >
            <Chip
              label={sublocationInfo?.creditCardFeeRate != null ? 'Custom CC fee' : 'Lease default CC fee'}
              size="small"
              color={sublocationInfo?.creditCardFeeRate != null ? 'primary' : 'default'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.PENNY_COST,
      value: checkNAOrReturnPercentValue(sublocationInfo?.pennyCostPercentage),
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.AREA_TAX_LOCATION,
      value: sublocationInfo?.areaTaxLocations?.areaTaxLocationName,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.TAX_EXEMPT_FLAG,
      value: <YesNoIndicator value={sublocationInfo?.areaTaxHistories?.[0]?.taxExemptFlag === true} />,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.LOC_PAYS_TAX,
      value: <YesNoIndicator value={sublocationInfo?.locPaysTaxFlag === true} />,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CALC_RENT,
      value: <YesNoIndicator value={sublocationInfo?.calculateRent === true} />,
    },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CREDIT_CARD_IN_COLLECTION,
      value: <YesNoIndicator value={sublocationInfo?.isCreditCardInCollection === true} />,
    },
    { label: SUB_LOCATION_DETAIL_LABEL.FEED_SOURCE, value: getFeedSourceDisplayText(sublocationInfo?.feedSource) },
    {
      label: SUB_LOCATION_DETAIL_LABEL.CREDIT_CASH_CONTROL,
      value: getCreditCashDisplayText(sublocationInfo?.creditCashControl),
    },
    ...(isArcadeOrGameRoomType(sublocationInfo?.productCategories, productCategoriesData)
      ? [{ label: SUB_LOCATION_DETAIL_LABEL.POSTING_LOCATION, value: sublocationInfo?.postingLocation }]
      : []),
  ];

  return <InformationPad data={fields} />;
};
export default SubLocationDetailAccordion;
